import React, { useId, useMemo, useRef, useState } from "react";
import COTIFY_LOGO from "../../assets/logo-cotify-horizontal-negro.png";
import { buildContactReportScene, contactLink, CONTACT_PT_TO_MM } from "./utils/contactReportLayout.js";
import "./ReportContactPage.css";

function ContactImage({ box, source, fallback = COTIFY_LOGO }) {
  const clipId = useId().replace(/:/g, "");
  const [failedSource, setFailedSource] = useState(null);
  const useFallback = !source || source === failedSource;
  const src = useFallback ? fallback : source;
  const padding = useFallback && box.key === "avatar" ? 5 : 0;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={box.x} y={box.y} width={box.width} height={box.height} rx={box.radius} />
        </clipPath>
      </defs>
      <image
        href={src}
        x={box.x + padding}
        y={box.y + padding}
        width={box.width - padding * 2}
        height={box.height - padding * 2}
        preserveAspectRatio={box.key === "avatar" && !useFallback ? "xMidYMid slice" : "xMidYMid meet"}
        clipPath={`url(#${clipId})`}
        data-contact-image={box.key}
        aria-label={box.key === "avatar" ? (useFallback ? "Logo de Cotify" : "Foto del asesor") : "Logo de la empresa"}
        onError={() => { if (!useFallback) setFailedSource(source); }}
      />
    </g>
  );
}


export function ReportContactTools({ photoUrl, onPhotoChange, showEditControls, onToggle }) {
  const inputRef = useRef(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [error, setError] = useState("");
  const changePhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setError("Elegí una foto JPG o PNG de hasta 10 MB.");
      return;
    }
    setError("");
    setPhotoLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      onPhotoChange(String(reader.result));
      setPhotoLoading(false);
    };
    reader.onerror = () => {
      setError("No se pudo leer la foto. Probá con otra imagen.");
      setPhotoLoading(false);
    };
    reader.readAsDataURL(file);
  };
  return (
    <>
      <div className="report-contact-tools">
        <button type="button" onClick={onToggle}>
          {showEditControls ? "Ver sin marcas de edición" : "Editar contacto"}
        </button>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={photoLoading}>
          {photoLoading ? "Cargando foto…" : "Cambiar foto"}
        </button>
        {photoUrl && <button type="button" onClick={() => onPhotoChange("")} disabled={photoLoading}>Usar logo de Cotify</button>}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg" aria-label="Foto del asesor para este informe" onChange={changePhoto} hidden />
      </div>
      {error && <span className="report-contact-photo-error" role="alert">{error}</span>}
    </>
  );
}

export default function ReportContactPage({ contact, branding, onChange, showEditControls = true }) {
  const { elements, fields, qrLink, profileFill } = useMemo(
    () => buildContactReportScene(contact, branding?.primary_color, showEditControls),
    [contact, branding?.primary_color, showEditControls],
  );
  return (
    <div className="report-contact-sheet">
      <svg
        className="report-contact-sheet__art"
        viewBox="0 0 320 297"
        role="img"
        aria-label={`Contacto del asesor: ${fields.map(field => field.value).filter(Boolean).join(". ")}`}
      >
        {elements.map((element, index) => {
          if (element.type === "text") {
            return <text key={index} x={element.x} y={element.y} fontSize={element.fontPt * CONTACT_PT_TO_MM} fontFamily="Helvetica, Arial, sans-serif" fontWeight={element.bold ? "bold" : "normal"} fill={element.color}>{element.value}</text>;
          }
          if (element.type === "image") {
            return <ContactImage key={element.key} box={element} source={element.key === "avatar" ? contact.photoUrl : branding?.logo_url} />;
          }
          if (element.type === "circle") {
            return <circle key={index} cx={element.x} cy={element.y} r={element.radius} fill={element.fill} />;
          }
          if (element.type === "path") {
            const d = element.commands.map(([op, ...points]) => op.toUpperCase() + points.map((point, pointIndex) => point * element.scale + (pointIndex % 2 ? element.y : element.x)).join(" ")).join(" ");
            return <path key={index} d={d} fill={element.fill || "none"} stroke={element.fill ? "none" : element.stroke} strokeWidth={element.strokeWidth} />;
          }
          return <rect key={index} x={element.x} y={element.y} width={element.width} height={element.height} rx={element.radius} fill={element.fill} stroke={element.stroke || "none"} strokeWidth={0.3} />;
        })}
      </svg>
      {fields.map(field => showEditControls ? (
        <textarea
          key={field.key}
          aria-label={field.label}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          rows={1}
          spellCheck={false}
          value={field.value}
          onChange={event => onChange(field.key, event.target.value.replace(/[\r\n]+/g, " "))}
          className="report-contact-sheet__input"
          style={{
            left: field.x * 2,
            top: field.y * 2,
            width: field.width * 2,
            height: field.height * 2,
            fontSize: field.fontPt * CONTACT_PT_TO_MM * 2,
            lineHeight: `${field.lineHeight * 2}px`,
            fontWeight: field.bold ? "bold" : "normal",
            "--field-fill": ["name", "title", "company"].includes(field.key) ? profileFill : "#fff",
          }}
        />
      ) : contactLink(field.key, field.value) ? (
        <a
          key={field.key}
          className="report-contact-sheet__link"
          href={contactLink(field.key, field.value)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${field.label}: ${field.value}`}
          style={{ left: field.x * 2, top: field.y * 2, width: field.width * 2, height: field.height * 2 }}
        />
      ) : null)}
      {qrLink && (
        <a
          className="report-contact-sheet__link report-contact-sheet__qr-link"
          href={qrLink.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir ubicación en Google Maps"
          style={{ left: qrLink.x * 2, top: qrLink.y * 2, width: qrLink.width * 2, height: qrLink.height * 2 }}
        />
      )}
    </div>
  );
}
